import { RelativeTime } from "@/components/changelog/relative-time"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import type { ChangelogEntry } from "@/types/changelog"
import { Bug, ChevronDown, ChevronRight, RefreshCw, Rocket, Settings, Zap } from "lucide-react"
import { useState } from "react"

interface ChangelogListProps {
  groupedEntries: Record<string, ChangelogEntry[]>;
}

const tagIcons = {
  feature: Rocket,
  bugfix: Bug,
  improvement: Zap,
  refactoring: RefreshCw,
  chore: Settings,
};

const tagColors = {
  feature: "border-transparent bg-blue-500/10 text-blue-700 dark:text-blue-400",
  bugfix: "border-transparent bg-red-500/10 text-red-700 dark:text-red-400",
  improvement: "border-transparent bg-green-500/10 text-green-700 dark:text-green-400",
  refactoring: "border-transparent bg-purple-500/10 text-purple-700 dark:text-purple-400",
  chore: "border-transparent bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
};

const MIN_VISIBLE_CHANGELOG_ENTRIES = 10;

function Code({ code }: { code: string }) {
  return <code className="bg-muted rounded px-1 font-mono text-xs">{code}</code>;
}

const formatLine = (line: string) => {
  const parts = line.split(/(`[^`]+`)/).filter(Boolean);
  return parts.map((part, index) => {
    if (part.startsWith("`") && part.endsWith("`")) {
      return <Code key={index} code={part.slice(1, -1)} />;
    }
    return part;
  });
};

type ChangelogFilter = "all" | "feature" | "improvement" | "bugfix";
type ChangelogFilterOption = {
  id: ChangelogFilter;
  label: string;
  icon: typeof Rocket | typeof Zap | typeof Bug | null;
};

function filterEntriesByType(entries: ChangelogEntry[], selectedFilter: ChangelogFilter) {
  if (selectedFilter === "all") return entries;
  return entries.filter((entry) => entry.tags.some((tag) => tag.type === selectedFilter));
}

function getDefaultExpandedMonths(
  groupedEntries: Record<string, ChangelogEntry[]>,
  selectedFilter: ChangelogFilter,
): Record<string, boolean> {
  const expandedMonths: Record<string, boolean> = {};
  let visibleEntriesCount = 0;

  for (const [month, entries] of Object.entries(groupedEntries)) {
    const visibleEntries = filterEntriesByType(entries, selectedFilter);

    if (visibleEntries.length === 0) {
      continue;
    }

    expandedMonths[month] = true;
    visibleEntriesCount += visibleEntries.length;

    if (visibleEntriesCount >= MIN_VISIBLE_CHANGELOG_ENTRIES) {
      break;
    }
  }

  return expandedMonths;
}

export function ChangelogList({ groupedEntries }: ChangelogListProps) {
  const [selectedFilter, setSelectedFilter] = useState<ChangelogFilter>("all");
  const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>(() =>
    getDefaultExpandedMonths(groupedEntries, "all"),
  );

  const toggleMonth = (month: string) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [month]: !prev[month],
    }));
  };

  const handleFilterChange = (filter: ChangelogFilter) => {
    setSelectedFilter(filter);
    setExpandedMonths(getDefaultExpandedMonths(groupedEntries, filter));
  };

  const filters: ChangelogFilterOption[] = [
    { id: "all", label: "All", icon: null },
    { id: "feature", label: "Features", icon: Rocket },
    { id: "improvement", label: "Improvements", icon: Zap },
    { id: "bugfix", label: "Bug Fixes", icon: Bug },
  ];

  // Check if any entries match the current filter
  const hasFilteredEntries = Object.values(groupedEntries).some(
    (entries) => filterEntriesByType(entries, selectedFilter).length > 0,
  );

  return (
    <>
      {/* Filters */}
      <div className="border-border border-b">
        <div className="mx-auto max-w-6xl px-6 py-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {filters.map((filter) => {
                const Icon = filter.icon;
                return (
                  <Button
                    key={filter.id}
                    variant={selectedFilter === filter.id ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => handleFilterChange(filter.id)}
                    className={`${
                      selectedFilter === filter.id
                        ? "border-primary/20 bg-primary/10 text-primary border"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    {Icon && <Icon className="mr-1.5 h-3.5 w-3.5" />}
                    {filter.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-6xl px-6 py-12">
        {hasFilteredEntries ? (
          <div className="space-y-12">
            {Object.entries(groupedEntries).map(([month, entries]) => {
              const filteredEntries = filterEntriesByType(entries, selectedFilter);
              if (filteredEntries.length === 0) return null;

              return (
                <div key={month} className="space-y-6">
                  <Button
                    variant="ghost"
                    onClick={() => toggleMonth(month)}
                    className="text-foreground hover:bg-muted hover:text-primary bg-background/80 sticky top-0 z-10 -ml-2 flex h-auto items-center gap-3 rounded-md px-3 py-2 text-2xl font-semibold backdrop-blur-sm"
                  >
                    {month}
                    {expandedMonths[month] ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                  </Button>

                  {expandedMonths[month] && (
                    <div className="space-y-4">
                      {filteredEntries.map((entry) => (
                        <Card
                          key={entry.sha}
                          id={entry.sha}
                          className="border-border bg-card/50 hover:border-muted-foreground/50 transition-colors"
                        >
                          <CardContent className="p-6">
                            <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                <div className="flex flex-wrap items-center gap-2">
                                  {entry.tags.map((tag, index) => {
                                    const Icon = tagIcons[tag.type];
                                    return (
                                      <Badge
                                        key={index}
                                        variant="outline"
                                        className={`${tagColors[tag.type]} flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs select-none`}
                                      >
                                        <Icon className="h-3 w-3" />
                                        {tag.label}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="text-muted-foreground text-sm">
                                <RelativeTime timestamp={entry.timestamp} />
                              </div>
                            </div>

                            <h3 className="text-foreground mb-4 text-xl whitespace-pre-line">
                              {formatLine(entry.title)}
                            </h3>

                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={entry.author.avatar} alt={entry.author.name} />
                                  <AvatarFallback className="bg-muted text-xs">
                                    {entry.author.name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-muted-foreground text-sm">{entry.author.name}</span>
                              </div>

                              {entry.stats && (
                                <div className="flex items-center gap-3 font-mono text-sm">
                                  <span className="font-mono text-green-700 dark:text-green-400">
                                    +{entry.stats.additions.toLocaleString()}
                                  </span>
                                  <span className="font-mono text-red-600 dark:text-red-400">
                                    -{entry.stats.deletions.toLocaleString()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="text-muted-foreground mb-4 text-6xl">
              {selectedFilter === "feature" && "🚀"}
              {selectedFilter === "improvement" && "⚡"}
              {selectedFilter === "bugfix" && "🐛"}
            </div>
            <h3 className="text-foreground mb-2 text-xl">
              No {filters.find((f) => f.id === selectedFilter)?.label.toLowerCase()} found
            </h3>
            <p className="text-muted-foreground max-w-md text-center">
              We don't have any {filters.find((f) => f.id === selectedFilter)?.label.toLowerCase()} in our changelog
              yet. Try selecting a different filter or check back later!
            </p>
            <Button variant="outline" size="sm" onClick={() => setSelectedFilter("all")} className="mt-6">
              View all entries
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
